/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";

import { AdaaHelper } from "../../../../core/utils/adaa-helper";
import { AdaaBoolean } from "../../../constants/enums";
import type { ParameterCatalog } from "../../../models";
import { LanguageService } from "../../../services";

type CheckboxValueType = "Y" | "N" | boolean | null | undefined;

@Component({
  selector: "adaa-form-checkbox",
  imports: [ReactiveFormsModule],
  templateUrl: "./form-checkbox.html",
  styleUrl: "./form-checkbox.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormCheckboxComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormCheckboxComponent),
    },
  ],
})
export class FormCheckboxComponent implements ControlValueAccessor {
  readonly languageService = inject(LanguageService);

  readonly inputControl = new FormControl();

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: CheckboxValueType) => void;
  private _disabledByInput = false;

  readonly useSwitch = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly label = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly clear = input<boolean>(false);
  readonly checkboxMode = input<"yesNo" | "trueFalse">("trueFalse");
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly boldLabel = input<boolean>(false);
  readonly checked = input<boolean>(false);
  readonly setDefaultValue = input<CheckboxValueType>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  constructor() {
    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      if (this.clear()) {
        untracked(() => this.inputControl.reset());
      }
    });

    effect(() => {
      if (this.checked()) {
        untracked(() => this.writeValue("Y"));
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      if (AdaaHelper.isDefined(value)) {
        untracked(() => this.writeValue(value!));
      }
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === AdaaBoolean.Y);
            this.inputControl.updateValueAndValidity();
          });
        }
      }
    });
  }

  public writeValue(obj: CheckboxValueType): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    if (this.checkboxMode() === "yesNo") {
      this.inputControl.setValue(obj === AdaaBoolean.Y);
    } else {
      this.inputControl.setValue(obj);
    }
  }

  public registerOnChange(fn: (value: CheckboxValueType) => void): void {
    this._onChange = fn;
    this.inputControl.valueChanges.pipe(this._untilDestroyed()).subscribe((value) => {
      if (this.checkboxMode() === "trueFalse") this._onChange(value);
      else this._onChange(value ? AdaaBoolean.Y : AdaaBoolean.N);
    });
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return (
      isRequired && {
        required: true,
      }
    );
  }

  public valueChanged(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.checkboxMode() === "trueFalse") {
      this.inputChanges.emit(target.checked);
    } else {
      this.inputChanges.emit(target.checked ? AdaaBoolean.Y : AdaaBoolean.N);
    }
  }
}
